import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import Banner from "../../components/ui/Banner/Banner.jsx";
import Tag from "../../components/ui/Tag/Tag.jsx";
import MarketingArt from "../../components/marketing/MarketingArt.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import { RATES, WEIGHTS } from "../../data/rates.js";
import { money } from "../../utils/format.js";
import SeoHead from "../../seo/SeoHead.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";
import { breadcrumbSchema } from "../../seo/schema.js";

const CATEGORIES = Object.keys(RATES);

const meta = ROUTE_META["/rates"];

const Rates = () => (
  <div className="max-w-[1180px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/rates"
        keywords={meta.keywords}
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Rates & weight categories", path: "/rates" }])]}
      />
    <PageHeader eyebrow="Form 2290, page 2 · Tax computation" title="TY2026 weight categories & rates">
      Tax is set by the IRS, not by EasyFile2290 — it's based on the vehicle's taxable gross weight and how many
      months it's used during the July 1, 2026 – June 30, 2027 period. The annual rates below are for a full
      12-month period; a vehicle first used after July pays a partial-period amount for the months remaining.
    </PageHeader>

    <MarketingArt src="rates.webp" className="w-full max-w-[600px] h-auto mt-7" />

    <div className="flex flex-wrap gap-2 mt-4">
      <Tag tone="default">Category A = 55,000 lb (minimum taxable weight)</Tag>
      <Tag tone="log">Logging = reduced rate</Tag>
      <Tag tone="susp">Category W = suspended, no tax</Tag>
    </div>

    <Panel className="overflow-hidden mt-6">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[560px]">
          <thead>
            <tr>
              {["Category", "Taxable gross weight", "Standard (annual)", "Logging (annual)"].map((h) => (
                <th
                  key={h}
                  className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map((cat) => (
              <tr key={cat}>
                <td className="p-3 border-b border-[var(--line)] font-mono font-semibold">{cat}</td>
                <td className="p-3 border-b border-[var(--line)] text-[var(--muted)]">{WEIGHTS[cat]} lb</td>
                <td className="p-3 border-b border-[var(--line)] font-mono">{money(RATES[cat][0])}</td>
                <td className="p-3 border-b border-[var(--line)] font-mono">{money(RATES[cat][1])}</td>
              </tr>
            ))}
            <tr>
              <td className="p-3 font-mono font-semibold">W</td>
              <td className="p-3 text-[var(--muted)]" colSpan={3}>
                Suspended — expected to be used 5,000 miles or less (7,500 for agricultural vehicles) during the
                period. No tax due, but the vehicle is still reported on Schedule 1.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>

    <Banner tone="info" className="mt-5 max-w-[720px]">
      A vehicle qualifies for the logging rate if it's used exclusively to transport products harvested from a
      forested site (or exclusively between locations on that site) and is registered as a logging vehicle under
      state law.
    </Banner>

    <Panel padded className="!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between gap-6 mt-[54px] !p-[30px]">
      <div>
        <h2 className="text-[22px] font-disp">See your exact tax</h2>
        <p className="text-[var(--muted)] mt-1">Add your trucks and EasyFile2290 figures the totals for you, automatically.</p>
      </div>
      <Button as={Link} to="/login?tab=up" size="lg" className="no-underline flex-none">
        Get started free <ArrowRight size={17} />
      </Button>
    </Panel>
  </div>
);

export default Rates;
